import { Box, Typography, useTheme } from "@mui/material";
import {
  Card,
  Chart2,
  Link1,
  Location,
  Notification,
  RecordCircle,
  Shop,
  TickCircle,
} from "iconsax-react";
import { useTranslation } from "react-i18next";

import { CreateShopProgressBar } from "@/components/Molecules";
import { getSocialIconColor } from "@/lib/createShop/socialIconColor";
import { formatPersianNumber } from "@/lib/format/persian";
import type { CreateShopPreviewSidebarProps } from "./CreateShopPreviewSidebar.types";
import * as styles from "./CreateShopPreviewSidebar.styles";

const benefitIcons = {
  link: Link1,
  card: Card,
  bell: Notification,
  chart: Chart2,
} as const;

export function CreateShopPreviewSidebar({
  logoPreview,
  watchedName,
  categoryLabelKey,
  bizTypeLabelKey,
  warehouseAddress,
  description,
  profileCompletion,
  connectedSocials,
  checklist,
  benefits,
}: CreateShopPreviewSidebarProps) {
  const { t } = useTranslation(["shops", "settings"]);
  const theme = useTheme();
  const name = watchedName.trim() || t("create.preview.defaultName");

  return (
    <Box sx={styles.root}>
      <Box sx={styles.card}>
        <Box sx={styles.header}>
          <Typography fontWeight={700}>{t("create.preview.title")}</Typography>
          <Typography color="primary" fontWeight={700}>
            {t("create.preview.completion", {
              percent: formatPersianNumber(profileCompletion),
            })}
          </Typography>
        </Box>

        <CreateShopProgressBar percent={profileCompletion} />

        <Box sx={styles.shop}>
          <Box sx={styles.avatar}>
            {logoPreview ? (
              <Box component="img" src={logoPreview} alt="" sx={styles.image} />
            ) : (
              name.charAt(0)
            )}
          </Box>
          <Box>
            <Typography fontWeight={700}>{name}</Typography>
            <Typography sx={styles.muted}>
              {categoryLabelKey
                ? t(`settings:${categoryLabelKey}`)
                : t("create.preview.categoryEmpty")}
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.divider} />

        <Box sx={styles.row}>
          <Shop size={16} variant="Bold" color={theme.palette.text.secondary} />
          <Typography sx={styles.muted}>
            {bizTypeLabelKey
              ? t(`settings:${bizTypeLabelKey}`)
              : t("create.preview.bizTypeEmpty")}
          </Typography>
        </Box>

        <Box sx={styles.row}>
          <Location size={16} variant="Bold" color={theme.palette.text.secondary} />
          <Typography sx={styles.muted}>
            {warehouseAddress || t("create.preview.addressEmpty")}
          </Typography>
        </Box>

        <Typography sx={styles.muted}>
          {description || t("create.preview.descriptionEmpty")}
        </Typography>

        <Box sx={styles.row}>
          {connectedSocials.map((social) => {
            const Icon = social.icon;

            return (
              <Icon
                key={social.key}
                size={16}
                variant="Bold"
                color={getSocialIconColor(social.colorToken, social.filled, theme)}
              />
            );
          })}
        </Box>
      </Box>

      <Box sx={styles.card}>
        <Typography fontWeight={700}>{t("create.checklist.title")}</Typography>

        {checklist.map((item) => (
          <Box key={item.labelKey} sx={styles.row}>
            {item.done ? (
              <TickCircle size={19} variant="Bold" color={theme.palette.success.main} />
            ) : (
              <RecordCircle size={19} variant="Linear" color={theme.palette.text.secondary} />
            )}
            <Typography sx={styles.muted}>{t(item.labelKey)}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={styles.card}>
        <Typography fontWeight={700}>{t("create.benefits.title")}</Typography>

        {benefits.map((benefit) => {
          const Icon = benefitIcons[benefit.iconKey as keyof typeof benefitIcons] ?? Link1;

          return (
            <Box key={benefit.labelKey} sx={styles.row}>
              <Icon size={19} variant="Bold" color={theme.palette.secondary.main} />
              <Typography sx={styles.muted}>{t(benefit.labelKey)}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
